import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/Button';
import { Logo, Wordmark } from '@/components/ui/Logo';

/**
 * The page the unsubscribe link in a reminder opens.
 *
 * **It asks before it acts**, which is not politeness — a link that unsubscribes
 * on being opened is a link that mail clients unsubscribe for you. Gmail and
 * Outlook both prefetch, so a GET that changed anything would quietly turn off
 * the reminders of people who never clicked. The button POSTs.
 *
 * Signed out, like the RSVP pages: somebody acting on a mail from three weeks ago
 * should not have to find their password first, and a 160-bit token in the link is
 * what authorises it.
 *
 * It answers the same for a token that matched nothing, because the server does —
 * saying "no such token" would make this a way to test guesses at one.
 */
export function UnsubscribePage() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const token = params.get('t') ?? '';
  const [state, setState] = useState<'asking' | 'sending' | 'done' | 'failed'>('asking');

  async function stop() {
    setState('sending');
    try {
      await api(`/public/unsubscribe?t=${encodeURIComponent(token)}`, {
        method: 'POST',
        auth: false,
      });
      setState('done');
    } catch {
      setState('failed');
    }
  }

  return (
    <main className="flex min-h-dvh items-center justify-center bg-cream">
      <div className="gutter w-full max-w-[520px]">
        <div className="panel flex flex-col gap-4 p-6">
          <div className="flex items-center gap-2">
            <Logo size={28} tone="leaf" />
            <Wordmark className="text-leaf" />
          </div>

          {state === 'done' ? (
            <>
              <h1 className="text-section">{t('unsub.doneTitle')}</h1>
              <p className="text-body text-leaf-soft">{t('unsub.doneBody')}</p>
            </>
          ) : (
            <>
              <h1 className="text-section">{t('unsub.title')}</h1>
              <p className="text-body text-leaf-soft">{t('unsub.body')}</p>
              {state === 'failed' && (
                <p className="text-body text-status-declinedFg">{t('unsub.failed')}</p>
              )}
              <div className="flex flex-wrap gap-3">
                <Button onClick={() => void stop()} disabled={state === 'sending' || !token}>
                  {state === 'sending' ? t('unsub.stopping') : t('unsub.stop')}
                </Button>
                <a
                  href="/app"
                  className="min-h-tap self-center text-caption text-leaf-soft underline underline-offset-4"
                >
                  {t('unsub.keep')}
                </a>
              </div>
            </>
          )}
        </div>
      </div>
    </main>
  );
}
